import React, { useState, useEffect } from "react";
import bannerimg from "../../assets/contact/bacground.png";
import {
  FileText,
  Shield,
  UserCog,
  HardDrive,
  Globe,
  Users,
  Calendar,
  CheckCircle,
  ScrollText,
  Scale,
  AlertTriangle,
  Lock,
  XCircle,
} from "lucide-react";

// --- Sidebar Navigation Item ---
const NavItem = ({ icon: Icon, title, isActive }) => {
  const activeClasses =
    "bg-gradient-to-r from-[#00AFEF] to-[#2F56A6] text-white shadow-lg";
  const inactiveClasses =
    "bg-white text-gray-800 shadow-md border-r-4 border-transparent hover:border-r-[#00AFEF]";

  return (
    <a
      href={`#${title.toLowerCase().replace(/\s/g, "-")}`}
      className={`flex items-center p-3 my-2 rounded-lg transition-all duration-300 ${
        isActive ? activeClasses : inactiveClasses
      }`}
    >
      <Icon
        className={`w-5 h-5 mr-3 flex-shrink-0 ${
          isActive ? "text-white" : "text-[#00AFEF]"
        }`}
      />
      <span className="font-medium text-base">{title}</span>
    </a>
  );
};

// --- Policy Detail Item ---
const PolicyDetailItem = ({ title, content }) => (
  <li className="flex items-start mb-4">
    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-1.5 mr-3 text-[#00AFEF]" />
    <p className="text-gray-600 text-[16px] leading-relaxed">
      <strong className="font-semibold text-[#2F56A6]">{title}:</strong>{" "}
      {content}
    </p>
  </li>
);

// --- Main Component ---
const TermsAndConditions = () => {
  const [activeSection, setActiveSection] = useState("Introduction");

const navItems = [
  { title: "Introduction", icon: FileText },
  { title: "User Terms", icon: Users },
  { title: "Eligibility", icon: Shield },
  { title: "Privacy", icon: Lock },
  { title: "Third-Party Integrations", icon: Globe },
  { title: "Security & Phishing Awareness", icon: AlertTriangle },
  { title: "Acceptable Use Policy", icon: UserCog },
  { title: "Intellectual Property", icon: ScrollText },
  { title: "Client Agreement & NDA", icon: HardDrive },
  { title: "Payments & Refunds", icon: Scale },
  { title: "Indemnification", icon: Shield },
  { title: "Disclaimer of Warranties", icon: XCircle },
  { title: "Limitation of Liability", icon: AlertTriangle },
  { title: "Termination of Access", icon: XCircle },
  { title: "Force Majeure", icon: Globe },
  { title: "Governing Law & Jurisdiction", icon: Calendar },
  { title: "Amendments", icon: FileText },
  { title: "Entire Agreement", icon: ScrollText },
  { title: "Contact Us", icon: Users },
];

  return (
    <>
      {/* Banner */}
      <div className="relative w-full h-[300px] md:h-auto min-h-52">
        <img
          src={bannerimg}
          alt="Terms and Conditions Banner"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 py-12 md:pb-0 xs:pb-1">
          <h1 className="text-white text-4xl md:text-6xl lg:text-[80px] font-normal font-goldman text-center leading-tight">
            Terms & Conditions
          </h1>
          <p className="text-[#D9D9D9] text-base md:text-xl lg:text-[22px] text-center max-w-2xl font-dmsans mt-2">
            Understand the terms governing the use of API Galaxy services.
          </p>
        </div>
      </div>

      {/* Main Section */}
      <div className="min-h-screen bg-white font-sans mt-[116px]">
        <div className="flex max-w-7xl mx-auto pb-20 pt-4 px-6 sm:px-8 -mt-20">
          {/* Sidebar */}
          <div className="w-[300px] p-4 bg-white rounded-xl mr-12 h-fit sticky top-6 hidden lg:block">
            {navItems.map((item) => (
  <div key={item.title} onClick={() => setActiveSection(item.title)}>
    <NavItem
      title={item.title}
      icon={item.icon}
      isActive={activeSection === item.title}
    />
  </div>
))}

          </div>

          {/* Main Content */}
          <div className="w-full lg:w-3/4 p-2">
            <h2 className="text-2xl md:text-3xl font-light text-gray-800 mb-6">
              PLEASE READ THESE TERMS AND CONDITIONS CAREFULLY.
            </h2>
            <p className="text-gray-600 text-[16px] mb-8 leading-relaxed">
              BY USING THIS WEBSITE AND THE SERVICES, YOU AGREE TO BE BOUND BY
              ALL OF THESE TERMS AND CONDITIONS.
            </p>

            <div className="space-y-10">
              {[
                {
                  title: "Introduction",
                  content:
                    "Welcome to API Galaxy, operated by Hexolt Life Solution Private Limited ('we,' 'us,' 'our'). These Terms govern your access to and use of our website, mobile applications, APIs, and associated services (the 'Platform').",
                },
                {
                  title: "User Terms",
                  content:
                    "These Terms remain effective as long as you access or use our services. Your continued use constitutes acceptance of these Terms.",
                },
                {
                  title: "Eligibility",
                  content:
                    "Our services are available only to individuals capable of entering legally binding contracts under the Indian Contract Act, 1872. Users must be at least 18 years old or have guardian consent.",
                },
                {
                  title: "Privacy",
                  content:
                    "We take your privacy seriously. Please refer to our Privacy Policy to understand how we collect and protect your data.",
                },
                {
                  title: "Third-Party Integrations",
                  content:
                    "Our website or APIs may include integrations with third-party tools. Use them at your discretion after reviewing their terms.",
                },
                {
                  title: "Security & Phishing Awareness",
                  content:
                    "Always ensure the URL begins with 'https://' before entering sensitive data. Report any suspicious sites to support@apigalaxy.in.",
                },
                {
                  title: "Acceptable Use Policy",
                  content:
                    "Users must not misuse the Platform for illegal, fraudulent, or unauthorized purposes. Violations may result in suspension or termination.",
                },
                {
                  title: "Intellectual Property",
                  content:
                    "All API documentation, software, and trademarks are owned by API Galaxy. Reproduction or modification without permission is prohibited.",
                },
                {
                  title: "Client Agreement & NDA",
                  content:
                    "Clients must sign a Service Agreement and NDA before accessing live APIs. Access is granted only post approval.",
                },
                {
                  title: "Payments & Refunds",
                  content:
                    "Payments are final and non-refundable unless due to service breach. New users may receive free credits as per policy.",
                },
                {
                  title: "Indemnification",
                  content:
                    "You agree to indemnify API Galaxy from claims or losses arising from misuse or unauthorized use of our services.",
                },
                {
                  title: "Disclaimer of Warranties",
                  content:
                    "All services are provided 'as is'. API Galaxy makes no warranties regarding uptime, accuracy, or suitability.",
                },
                {
                  title: "Limitation of Liability",
                  content:
                    "API Galaxy is not liable for indirect or consequential damages. Liability is capped to the fees paid in the last six months.",
                },
                {
                  title: "Termination of Access",
                  content:
                    "We reserve the right to restrict or terminate your access at any time without notice if Terms are violated.",
                },
                {
                  title: "Force Majeure",
                  content:
                    "API Galaxy is not liable for delays or failures beyond its control including natural disasters or technical disruptions.",
                },
                {
                  title: "Governing Law & Jurisdiction",
                  content:
                    "These Terms are governed by Indian laws under the exclusive jurisdiction of Nagpur, Maharashtra courts.",
                },
                {
                  title: "Amendments",
                  content:
                    "We may update these Terms periodically. Continued use constitutes acceptance of the updated Terms.",
                },
                {
                  title: "Entire Agreement",
                  content:
                    "These Terms, Privacy Policy, and NDA constitute the full agreement between you and API Galaxy.",
                },
                {
                  title: "Contact Us",
                  content:
                    "For any questions or concerns, email us at support@apigalaxy.in or visit www.apigalaxy.in.",
                },
              ].map((section, index) => (
                <div id={section.title.toLowerCase().replace(/\s/g, "-")} key={index}>
                  <h3 className="text-xl font-semibold text-[#2F56A6] mb-3">
                    {section.title}
                  </h3>
                  <p className="text-gray-600 text-[16px] leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="h-40 w-full" />
      </div>
    </>
  );
};

export default TermsAndConditions;
